import { signal } from "@preact/signals";
import type { FullJoin } from "../api/types";
import { refreshMe } from "./auth";
import { navigate, resetTo } from "./route";

/**
 * Game session contract between the menus (this module) and the game session of a later phase.
 *
 * Menus call `startGame(join)` after a successful quickplay / create / join / ranked-found
 * response. That stores the `JoinResponse` (`room` + `ticket`) in `gameJoin`, sets `gameStatus` to "connecting" and
 * shows the Game screen. The game session reacts to `gameJoin` becoming non-null or being replaced
 * (the error panel's Retry stores a fresh ticket for the same room; a ticket is single use and
 * expires after 30 s), drives `gameStatus` through "loading" and "playing" (or "error" with
 * `gameError`), and must tear everything down when `gameJoin` becomes null.
 *
 * While "playing" the menu overlay renders nothing, so the canvas and #hud-root receive input.
 * The session calls `leaveGame()` when the player leaves the match.
 */
export type GameStatus = "idle" | "connecting" | "loading" | "playing" | "error";

/** The `JoinResponse` ({room, ticket}) of the match being joined. */
export type GameJoin = FullJoin & {
  /** Password the player entered for a protected room, so "Retry" can fetch a fresh ticket. */
  password?: string;
};

export const gameStatus = signal<GameStatus>("idle");
export const gameJoin = signal<GameJoin | null>(null);
/** Message shown on the error panel when `gameStatus` is "error". */
export const gameError = signal<string>("");
/** 0..1 while the map of the match builds and its shaders compile (`gameStatus` is "loading"). */
export const gameLoadProgress = signal(0);

export function startGame(join: FullJoin, password?: string): void {
  // Status first: setting `gameJoin` runs the session effect synchronously, and a failure there
  // (no WebGL 2) sets "error" that must not be overwritten afterwards.
  gameError.value = "";
  gameStatus.value = "connecting";
  gameJoin.value = { ...join, password };
  navigate("game");
}

/** Leave the match (or abandon a failed connection) and return to the main menu. */
export function leaveGame(): void {
  gameJoin.value = null;
  gameStatus.value = "idle";
  gameError.value = "";
  resetTo("main");
  // XP, level and rating may have changed; a failed refresh keeps the old numbers.
  void refreshMe().catch(() => undefined);
}
